import { computed, onMounted, toRef, type Ref } from "vue";
import { useProductAnalytics } from "./useProductAnalytics";
import { useProductAttributes } from "./useProductAttributes";
import { useProductDelivery } from "./useProductDelivery";
import { useProductOverlays } from "./useProductOverlays";
import { useProductReviews } from "./useProductReviews";
import { useProductReviewDisplay } from "./useProductReviewDisplay";
import { useProductStickyBar } from "./useProductStickyBar";
import { useSpecPopover } from "./useSpecPopover";
import { useViewportMode } from "./useViewportMode";
import { finalSaleState } from "../../../../../utils/productSeoFacts";
import type { ProductSnapshot } from "../../../../../types/productSnapshot";

type ProductPageProps = {
  product: ProductSnapshot | null;
  loading?: boolean;
};

export function useProductPage(
  props: ProductPageProps,
  emits: {
    (event: "click:cart"): void;
    (event: "click:wishlist"): void;
  }
) {
  const productRef = toRef(props, "product") as Ref<ProductSnapshot | null>;
  const legacyProductRef = productRef as Parameters<
    typeof useProductReviews
  >[0];
  const loadingRef = computed(() => !!props.loading) as Ref<boolean>;

  const reviews = useProductReviews(legacyProductRef);
  const attributes = useProductAttributes(legacyProductRef);
  const analytics = useProductAnalytics();
  const delivery = useProductDelivery();
  const specPopover = useSpecPopover();

  const reviewDisplay = useProductReviewDisplay(
    reviews.productReviews,
    reviews.productRating
  );

  let overlays!: ReturnType<typeof useProductOverlays>;

  const viewport = useViewportMode(() => {
    overlays?.syncOverlaysOnResize();
  });

  overlays = useProductOverlays(viewport, reviews.hasProductReviews);

  const stickyBar = useProductStickyBar(legacyProductRef, loadingRef, viewport);

  const finalSaleStatus = computed(() =>
    finalSaleState(props.product?.finalSale)
  );
  const isFinalSale = computed(() => finalSaleStatus.value === "TRUE");

  // add_to_cart is sent by the control that dispatches the cart mutation, once
  // that mutation is confirmed (new-product-buy, ProductGiftOption). Tracking
  // here, on click, counted failed additions.
  const handleAddToCart = () => {
    emits("click:cart");
  };

  const handleAddToWishlist = () => {
    emits("click:wishlist");
  };

  onMounted(() => {
    setTimeout(() => {
      if (!props.product?.id) return;
      analytics.trackViewContent(props.product);
    }, 100);
  });

  return {
    ...viewport,
    ...reviews,
    ...reviewDisplay,
    ...attributes,
    ...overlays,
    ...delivery,
    ...specPopover,
    ...stickyBar,
    finalSaleStatus,
    isFinalSale,
    handleAddToCart,
    handleAddToWishlist,
  };
}
